/**
 * BaseNetworkContext - Estado global para vías base del IGN
 *
 * @module core/spatial
 * @version 1.0
 * @created 2026-03-01
 *
 * @description
 * Context similar a GridsContext pero para vías base de referencia.
 * Maneja estado de interactividad, selección y hover de vías.
 */

import { createContext, useContext, useState, ReactNode } from 'react';

/**
 * Tipo de vía base con atributos del IGN
 */
export interface BaseRoad {
  id: string;
  category: string;
  rtn: string | null;       // Número de ruta
  rst: number | null;       // Tipo de revestimiento (1=pavimentada)
  typ: number | null;       // Código de tipo
  hct: number | null;       // Categoría de circulación
  fdc: string | null;       // Fuente de datos
  gid: number | null;       // ID geográfico
}

/**
 * Estado del contexto
 */
export interface BaseNetworkContextValue {
  /** Si las vías base son interactivas (responden a clicks) */
  isBaseNetworkInteractive: boolean;
  setIsBaseNetworkInteractive: (value: boolean) => void;

  /** Vías cargadas del proyecto actual */
  roads: BaseRoad[];
  setRoads: (roads: BaseRoad[]) => void;

  /** ID de vía en hover (para highlight) */
  hoveredRoadId: string | null;
  setHoveredRoadId: (id: string | null) => void;

  /** ID de vía seleccionada (para panel de detalles) */
  selectedRoadId: string | null;
  setSelectedRoadId: (id: string | null) => void;

  /** Si el panel de vías está abierto */
  isRoadPanelOpen: boolean;
  setIsRoadPanelOpen: (value: boolean) => void;
}

const BaseNetworkContext = createContext<BaseNetworkContextValue | undefined>(undefined);

/**
 * Provider del contexto
 */
export function BaseNetworkProvider({ children }: { children: ReactNode }) {
  const [isBaseNetworkInteractive, setIsBaseNetworkInteractive] = useState(false);
  const [roads, setRoads] = useState<BaseRoad[]>([]);
  const [hoveredRoadId, setHoveredRoadId] = useState<string | null>(null);
  const [selectedRoadId, setSelectedRoadId] = useState<string | null>(null);
  const [isRoadPanelOpen, setIsRoadPanelOpen] = useState(false);

  return (
    <BaseNetworkContext.Provider
      value={{
        isBaseNetworkInteractive,
        setIsBaseNetworkInteractive,
        roads,
        setRoads,
        hoveredRoadId,
        setHoveredRoadId,
        selectedRoadId,
        setSelectedRoadId,
        isRoadPanelOpen,
        setIsRoadPanelOpen,
      }}
    >
      {children}
    </BaseNetworkContext.Provider>
  );
}

/**
 * Hook para usar el contexto
 */
export function useBaseNetwork(): BaseNetworkContextValue {
  const context = useContext(BaseNetworkContext);
  if (!context) {
    throw new Error('useBaseNetwork must be used within BaseNetworkProvider');
  }
  return context;
}
